import React, { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Formik, FormikHelpers } from 'formik';
import { Col, Row } from 'react-bootstrap';
import { createSellerContactForm } from '../../../utils/ajax/sellerContactFormAjax';
import { SellerContactFormRequest } from '../../../structures/requests/sellerContactForm';
import TextField from '../Partials/TextField';
import PhoneField from '../Partials/PhoneField';
import RecaptchaDisclaimer from '../Partials/RecaptchaDisclaimer';
import { SellerContactFormData } from '../../../structures/models/listings';
import SellerContactValidation from './validation';

interface FormValues {
  seller_id: number,
  name: string,
  email: string,
  phone: string,
  listing_name: string,
  message: string,
}

interface Props extends SellerContactFormData {
  sellerId: number,
  name?: string,
}

const SellerContactForm: React.FC<Props> = ({
  name,
  sellerId,
  listing,
  currentUser,
  displayContext,
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleReCaptchaVerify = async () => {
    if (!executeRecaptcha) {
      return '';
    }

    return executeRecaptcha('submit');
  };
  const formProps = {
    initialValues: {
      seller_id: sellerId,
      name: currentUser?.fullName || '',
      email: currentUser?.email || '',
      phone: currentUser?.phone || '',
      listing_name: listing?.name || '',
      message: '',
    },
    validateOnChange: false,
    validateOnBlur: true,
  };
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const isListingPage = displayContext === 'listing';

  const onSubmit = async (
    values: FormValues,
    helpers: FormikHelpers<any>,
  ) => {
    setLoading(true);
    let newValues: SellerContactFormRequest = {
      recaptcha: await handleReCaptchaVerify(),
      ...values,
    };

    if (listing) {
      newValues = {
        listing_id: listing.id,
        listing_type: listing.type,
        ...newValues,
      };
    }

    createSellerContactForm(newValues)
      .then(() => {
        setLoading(false);
        setSubmitted(true);
      })
      .catch(({ response }) => {
        setLoading(false);
        if (response?.status === 422) {
          const { data: { errors } } = response;
          Object.keys(errors)
            .forEach((key) => {
              helpers.setFieldError(key, errors[key]);
            });
        } else {
          helpers.setFieldError('name', 'An error occurred. Please try again later.');
        }
      });
  };

  // Hide form when submitted
  if (submitted) {
    return (
      <div className="alert alert-success">
        Your message has been sent
      </div>
    );
  }

  // Render form
  return (
    <div className="mt-3">
      {!!name && (
        <p className="blue-txt basic-family">{`Send a message to ${name} using the form below:`}</p>
      )}
      <Formik
        {...formProps}
        validationSchema={SellerContactValidation}
        onSubmit={onSubmit}
      >
        {(formikProps) => {
          const {
            handleSubmit,
            submitCount,
            errors,
          } = formikProps;
          const errorDisplay: Array<React.ReactElement> = [];
          if (submitCount) {
            Object.keys(errors)
              .forEach((errIndex) => {
                errorDisplay.push(
                  <div key={errIndex} className="alert alert-danger w-100">
                    {errors[errIndex]}
                  </div>,
                );
              });
          }
          return (
            <>
              <Row>
                <Col xs={12} md={isListingPage ? 4 : 6}>
                  <TextField
                    showLabel={false}
                    formikProps={formikProps}
                    fieldName="name"
                    visibleName="Your Name"
                  />
                </Col>

                {!isListingPage && (
                  <Col xs={12} md={6}>
                    <TextField
                      showLabel={false}
                      formikProps={formikProps}
                      fieldName="listing_name"
                      visibleName="Puppy Name"
                    />
                  </Col>
                )}

                <Col xs={12} md={isListingPage ? 4 : 6}>
                  <TextField
                    showLabel={false}
                    formikProps={formikProps}
                    fieldName="email"
                    visibleName="Email"
                  />
                </Col>

                <Col xs={12} md={isListingPage ? 4 : 6}>
                  <PhoneField
                    showLabel={false}
                    placeholder="Phone #"
                    formikProps={formikProps}
                    fieldName="phone"
                    visibleName="Phone"
                  />
                </Col>

                <Col xs={12}>
                  <TextField
                    showLabel={false}
                    formikProps={formikProps}
                    fieldName="message"
                    visibleName="Message"
                    showAsTextarea={true}
                  />
                </Col>

              </Row>

              <Row>
                {errorDisplay}
              </Row>

              <Row className="pb-3">
                <div className="col">
                  <button
                    type="submit"
                    className="btn btn-lg btn-primary text-uppercase basic-family"
                    onClick={() => {
                      handleSubmit();
                    }}
                    disabled={loading || !executeRecaptcha}
                  >
                    <span className="mr-2 fa fa-envelope" aria-hidden="true" />
                    Send Message
                  </button>
                </div>
              </Row>
            </>
          );
        }}
      </Formik>
      <RecaptchaDisclaimer />
    </div>
  );
};

export default SellerContactForm;
